import { normalize } from 'normalizr'

import Raven from 'lib/raven'
import api from 'lib/api'
import * as userUtil from 'utils/user-utils'
import * as schema from 'lib/schema'
import * as appActions from './app'

const SET_CREDENTIALS = 'crush/user/SET_CREDENTIALS'
const SET_MATCHES = 'crush/user/SET_MATCHES'
const SET_PROFILE = 'crush/user/SET_PROFILE'
const SET_PROFILE_PICTURES = 'crush/user/SET_PROFILE_PICTURES'
const SET_SETTINGS = 'crush/user/SET_SETTINGS'

const initialState = {
  credentials: {},
  profile: {},
  matches: []
}

if (process.env.NODE_ENV === 'test') {
  const { threadContext } = require('../messengerExtensions')
  initialState.credentials = { ...threadContext }
}

export default function (state = initialState, action) {
  switch (action.type) {
    case SET_CREDENTIALS:
      return {
        ...state,
        credentials: action.credentials
      }

    case SET_PROFILE:
      return {
        ...state,
        profile: action.profile
      }

    case SET_PROFILE_PICTURES:
      return {
        ...state,
        profile: {
          ...state.profile,
          pictures: action.pictures
        }
      }

    case SET_MATCHES:
      return {
        ...state,
        matches: action.matchIds
      }

    case SET_SETTINGS:
      return {
        ...state,
        profile: {
          ...state.profile,
          ...action.settings
        }
      }

    default:
      return state
  }
}

export const setCredentials = credentials => ({
  type: SET_CREDENTIALS,
  credentials
})

export const setProfile = profile => ({
  type: SET_PROFILE,
  profile: {
    ...profile,
    pictures: userUtil.cleanPictures(profile.pictures, profile.profilePic)
  }
})

export const setProfilePictures = (pictures, fallbackPictureUrl) => ({
  type: SET_PROFILE_PICTURES,
  pictures: userUtil.cleanPictures(pictures, fallbackPictureUrl)
})

export const setMatches = matchIds => ({
  type: SET_MATCHES,
  matchIds
})

export const setSettings = settings => ({
  type: SET_SETTINGS,
  settings
})

export const fetchUserProfile = () => (dispatch, getState) => {
  const { credentials, profile } = getState().user
  // Maybe we need here a refresh-flag or something similar. Practically we do not need the location in the client, but only the information,
  // if the user has a location at the server to calculate distances.
  if (profile.id && profile.lat) return Promise.resolve(profile)

  return api.profile
    .getMe(credentials)
    .then(profile => {
      dispatch(setProfile(profile))

      Raven.setUserContext({
        id: profile.id,
        username: `${profile.firstName} ${profile.lastName}`,
        senderId: credentials.psid
      })

      return profile
    })
    .catch(error => {
      dispatch(appActions.showFetchError())
      Raven.captureException(error)
    })
}

export const triggerAgeSubmit = () => (dispatch, getState) => {
  const { credentials } = getState().user

  return api.profile
    .requestAgeSubmit(credentials)
    .catch(error => {
      dispatch(appActions.showFetchError())
      Raven.captureException(error)
    })
    .then(() => api.MessengerExtensions.requestCloseBrowser())
}

export const triggerLocationChange = () => (dispatch, getState) => {
  const { credentials } = getState().user

  return api.profile
    .requestLocationChange(credentials)
    .catch(error => {
      dispatch(appActions.showFetchError())
      Raven.captureException(error)
    })
    .then(() => api.MessengerExtensions.requestCloseBrowser())
}

export const uploadImage = (position, image) => (dispatch, getState) => {
  const { credentials, profile } = getState().user

  const newPicture = { url: image.preview, position, uploading: true }
  const overridePicture = profile.pictures.find(pic => {
    if (!pic) return false
    return pic.position === position
  })

  let pictures
  if (overridePicture) {
    pictures = profile.pictures.map(pic => {
      if (pic && pic.position === position) return newPicture
      return pic
    })
  } else {
    pictures = [...profile.pictures, newPicture]
  }

  dispatch(setProfilePictures(pictures, profile.profilePic))

  return api.profile
    .uploadImage(credentials, position, image, overridePicture)
    .then(updatedProfile => {
      dispatch(setProfile(updatedProfile))
      return updatedProfile
    })
    .catch(error => {
      dispatch(appActions.showFetchError())
      Raven.captureException(error)
    })
}

export const deleteImage = position => (dispatch, getState) => {
  const { credentials, profile } = getState().user
  const picture = profile.pictures[position]

  const pictures = profile.pictures.map(pic => {
    if (pic && pic.position === position) return { ...pic, uploading: true }
    return pic
  })

  dispatch(setProfilePictures(pictures, profile.profilePic))

  return api.profile
    .deleteImage(picture.url, credentials)
    .then(updatedUser => {
      dispatch(setProfile(updatedUser))
      return updatedUser
    })
    .catch(error => {
      dispatch(appActions.showFetchError())
      Raven.captureException(error)
    })
}

export const fetchMatches = () => (dispatch, getState) => {
  const { credentials } = getState().user

  return api.profile
    .getMatches(credentials)
    .then(({ matches }) => {
      const { result, entities } = normalize(matches, [schema.crush])
      dispatch(appActions.addEntities(entities))
      dispatch(setMatches(result))

      return matches
    })
    .catch(error => {
      dispatch(appActions.showFetchError())
      Raven.captureException(error)
    })
}

export const deleteMatch = match => (dispatch, getState) => {
  const { credentials } = getState().user

  return api.profile
    .deleteMatch(match, credentials)
    .then(() => {
      const matches = getState().user.matches.filter((m) => { return m !== match.senderId })

      dispatch(setMatches(matches))

      return matches
    })
    .catch(error => {
      dispatch(appActions.showFetchError())
      Raven.captureException(error)
    })
}

export const updateSettings = settings => (dispatch, getState) => {
  const { credentials } = getState().user

  dispatch(setSettings(settings))
  return api.profile.updateSettings(settings, credentials).catch(error => {
    dispatch(appActions.showFetchError())
    Raven.captureException(error)
  })
}

export const requestDeleteAccount = () => (dispatch, getState) => {
  const { credentials } = getState().user

  return api.profile
    .requestDeleteAccount(credentials)
    .then(() => api.MessengerExtensions.requestCloseBrowser())
    .catch(error => {
      dispatch(appActions.showFetchError())
      Raven.captureException(error)
    })
}

export const addFacebookUserData = facebookUserData => (dispatch, getState) => {
  const { credentials } = getState().user

  return api.profile
    .addFacebookUserData(facebookUserData, credentials)
    .then(updatedProfile => {
      dispatch(setProfile(updatedProfile))
      return updatedProfile
    })
    .catch(error => {
      dispatch(appActions.showFetchError())
      Raven.captureException(error)
    })
}



// WEBPACK FOOTER //
// ./src/lib/store/user.js